import React, {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import UserService, {
  EnrollFactorResponse,
  EnrollProfile,
  Factor,
  FACTORS_TO_SHOW,
  FactorStatus,
  STAGE,
  SuccessStatusResponse,
  UppUser,
} from "@app/service/user";
import {
  FetchResult,
  isApolloError,
  useLazyQuery,
  useMutation,
} from "@apollo/client";
import {
  NotificationContext,
  ProfileContext,
  UserContext,
} from "@app/provider";
import { ResultCodes } from "@app/service/resultCodes";
import { useDialog } from "@natera/platform/lib/hooks";
import {
  FactorDisableModal,
  FactorSuccessModal,
} from "@app/components/factorList";
import { routes } from "@app/routing";
import R from "ramda";
import { HeapEventLocation } from "@app/provider/types";
import {
  getGraphQLErrorBodies,
  getGraphQLErrorCode,
} from "@app/provider/error/graphQLErrorCode";

export interface User {
  id?: string;
  patientUID?: string;
  stage?: STAGE;
  email?: string;
}

export enum LoginStatus {
  FAIL = "FAIL",
  SUCCESS = "SUCCESS",
}

interface ErrorResponse {
  message: string;
}

interface ActivateUserFactor {
  success: boolean;
}

export type FactorsObject = {
  [key in FACTORS_TO_SHOW]?: Factor | EnrollFactorResponse;
};

export interface MfaSetupController {
  isLoading: boolean;
  enrolledFactor: EnrollFactorResponse | undefined;
  isFactorActivated: boolean;
  setIsFactorActivated: Dispatch<SetStateAction<boolean>>;
  isUpdatePhoneAction: boolean;
  setIsUpdatePhoneAction: Dispatch<SetStateAction<boolean>>;
  factorList: Factor[] | undefined;
  enrolledFactorList: Factor[] | undefined;
  isUserInMFAGroup: boolean;
  loadIsUserInMFAGroup: () => void;
  isFirstSetup: boolean;
  showError: (err?: ErrorResponse) => void;
  handleSkip: () => Promise<void>;
  handleFactorSetup: (
    factor: Factor | undefined,
    isActive: boolean,
    isUserInMFAGroup: boolean
  ) => Promise<void>;
  enrollUserFactor: (
    factorType: string,
    provider: string,
    profile?: EnrollProfile
  ) => Promise<void>;
  resendUserFactorCode: () => void;
  activateUserFactor: (
    passCode: string,
    heapEventLocation?: HeapEventLocation
  ) => Promise<FetchResult<{ activateUserFactor: ActivateUserFactor }>>;
  disableUserFactors: () => Promise<
    FetchResult<{ disableUserFactors: SuccessStatusResponse }>
  >;
  loadFactorList: () => void;
  resetActivatedFactor: () => void;
  loadEnrolledFactorList: () => Promise<Factor[]>;
}

export const Context = React.createContext<MfaSetupController>({
  isLoading: false,
  enrolledFactor: undefined,
  isFactorActivated: false,
  setIsFactorActivated: () => undefined,
  isUpdatePhoneAction: false,
  setIsUpdatePhoneAction: () => undefined,
  factorList: undefined,
  enrolledFactorList: undefined,
  isUserInMFAGroup: false,
  loadIsUserInMFAGroup: () => undefined,
  isFirstSetup: false,
  showError: () => undefined,
  handleSkip: async () => undefined,
  handleFactorSetup: async () => undefined,
  enrollUserFactor: async () => undefined,
  resendUserFactorCode: () => undefined,
  activateUserFactor: async () => ({}),
  disableUserFactors: async () => ({}),
  loadFactorList: () => undefined,
  loadEnrolledFactorList: async () => [],
  resetActivatedFactor: () => undefined,
});

const messages = defineMessages({
  userFactorIsAlreadySetup: {
    id: "userFactorIsAlreadySetup",
    defaultMessage: "Factor is already set up",
  },
});

const FIRST_STEP_STAGES = [
  STAGE.BLANK,
  STAGE.MFA_SETUP,
  STAGE.COMMUNICATION_PREFERENCES,
];

Context.displayName = "MfaSetupContext";

const MfaSetupProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const [enrollFactorResponse, setEnrollFactorResponse] =
    useState<EnrollFactorResponse>();
  const { uppUser, loadProfile } = useContext(UserContext);
  const { getProfile, skipCommunicationPreferencesStep } =
    useContext(ProfileContext);
  const [isFactorActivated, setIsFactorActivated] = useState<boolean>(false);
  const [isUpdatePhoneAction, setIsUpdatePhoneAction] =
    useState<boolean>(false);
  const { addNotification, clear } = useContext(NotificationContext);
  const factorSuccessModal = useDialog(FactorSuccessModal);
  const factorDisableModal = useDialog(FactorDisableModal);

  const isFirstSetup = useMemo(() => {
    const stage = uppUser?.stage;
    return FIRST_STEP_STAGES.includes(stage as STAGE);
  }, [uppUser]);

  const showError = (err?: Error) => {
    if (err && isApolloError(err) && err.graphQLErrors?.length > 0) {
      const code = getGraphQLErrorCode(err);
      switch (code) {
        case ResultCodes.FACTOR_IS_ALREADY_SETUP:
          addNotification({
            message: intl.formatMessage(messages.userFactorIsAlreadySetup),
            type: "error",
          });
          break;
        default:
          addNotification({
            type: "error",
          });
      }
    } else {
      addNotification({
        type: "error",
      });
    }
  };

  const [skipMfa, { loading: skipMfaIsLoading }] = useMutation<{
    updateUser: UppUser;
  }>(UserService.updateUser(), {
    update: (cache, { data }) => {
      const { updateUser } = data || {};
      const { profile } =
        cache.readQuery<{ profile: UppUser }>({
          query: UserService.loadProfile(),
        }) || {};
      cache.writeQuery({
        query: UserService.loadProfile(),
        data: { profile: { ...profile, ...updateUser } },
      });
    },
  });

  const [loadFactorList, { loading: factorListIsLoading, data: factorList }] =
    useLazyQuery<{
      factorList: Factor[];
    }>(UserService.getUserFactorList(), {
      fetchPolicy: "network-only",
    });

  const [
    loadEnrolledFactorList,
    { loading: enrolledFactorListIsLoading, data: enrolledFactorList },
  ] = useLazyQuery<{ enrolledFactorList: Factor[] }>(
    UserService.getEnrolledUserFactorList(),
    {
      fetchPolicy: "network-only",
    }
  );

  const [
    loadIsUserInMFAGroup,
    { loading: isUserInMFAGroupLoading, data: isUserInMFAGroup },
  ] = useLazyQuery(UserService.isUserInMFAGroup(), {
    fetchPolicy: "network-only",
  });

  const [
    enrollUserFactor,
    {
      loading: enrollUserIsLoading,
      data: enrollFactorResp,
      error: enrollFactorErr,
    },
  ] = useMutation<{
    enrollUserFactor: EnrollFactorResponse;
  }>(UserService.enrollUserFactor());

  const [
    resendUserFactorCode,
    {
      loading: resendUserFactorCodeIsLoading,
      data: resendFactorCodeResp,
      error: resendFactorErr,
    },
  ] = useMutation<{
    resendUserFactorCode: EnrollFactorResponse;
  }>(UserService.resendUserFactorCode());

  const [
    activateUserFactor,
    { loading: activateUserFactorIsLoading, data: activateFactorResponse },
  ] = useMutation<{
    activateUserFactor: SuccessStatusResponse;
  }>(UserService.activateUserFactor());

  const [disableUserFactors, { loading: disableUserFactorsIsLoading }] =
    useMutation<{ disableUserFactors: SuccessStatusResponse }>(
      UserService.disableUserFactors()
    );

  useEffect(() => {
    if (enrollFactorResp) {
      setEnrollFactorResponse(enrollFactorResp.enrollUserFactor);
      if (
        enrollFactorResp.enrollUserFactor.status === FactorStatus.ACTIVE &&
        enrollFactorResp.enrollUserFactor.factorType !== FACTORS_TO_SHOW.email
      ) {
        setIsFactorActivated(true);
      }
    }
  }, [enrollFactorResp]);

  useEffect(() => {
    if (resendFactorCodeResp) {
      setEnrollFactorResponse(resendFactorCodeResp.resendUserFactorCode);
    }
  }, [resendFactorCodeResp]);

  useEffect(() => {
    if (activateFactorResponse?.activateUserFactor?.success) {
      setIsFactorActivated(true);
    }
  }, [activateFactorResponse]);

  useEffect(() => {
    if (enrollFactorErr) {
      const errorCode = getGraphQLErrorCode(enrollFactorErr);

      if (errorCode === ResultCodes.CODE_WAS_RECENTLY_SENT_ERROR) {
        const enrolledSms = enrolledFactorList?.enrolledFactorList
          ? R.find(
              (factor: Factor) => factor.factorType === FACTORS_TO_SHOW.sms,
              enrolledFactorList?.enrolledFactorList
            )
          : undefined;

        if (enrolledSms && enrolledSms.id) {
          setEnrollFactorResponse(enrolledSms as EnrollFactorResponse);
        }
      } else {
        showError(enrollFactorErr);
      }
    }
  }, [enrollFactorErr]);

  useEffect(() => {
    if (resendFactorErr) {
      showError(resendFactorErr);
    }
  }, [resendFactorErr]);

  const handleFactorActivated = (factorType: FACTORS_TO_SHOW) => {
    if (factorType === FACTORS_TO_SHOW.email) {
      navigate(routes.securitySettings, {
        replace: true,
        state: { emailFactorActivated: true },
      });
    } else if (isUpdatePhoneAction) {
      navigate(routes.contactDetails, {
        replace: true,
        state: {
          phoneUpdated: true,
        },
      });
    } else {
      navigate(routes.securitySettings, {
        replace: true,
        state: {
          phoneFactorActivated: true,
        },
      });
    }
  };

  useEffect(() => {
    if (enrollFactorResponse && isFactorActivated) {
      const isOnSecuritySettings =
        location.pathname === routes.securitySettings;
      const isPhoneFactor =
        enrollFactorResponse.factorType === FACTORS_TO_SHOW.sms ||
        enrollFactorResponse.factorType === FACTORS_TO_SHOW.call;

      if (isPhoneFactor) {
        if (isUpdatePhoneAction) {
          handleFactorActivated(enrollFactorResponse.factorType);
          return;
        }

        if (isFirstSetup) {
          if (!isOnSecuritySettings) {
            void (async () => {
              try {
                await loadProfile();
                navigate(routes.home + location.search, { replace: true });
              } catch (error) {
                showError(error);
                handleFactorActivated(enrollFactorResponse.factorType);
              }
            })();
          } else {
            handleFactorActivated(enrollFactorResponse.factorType);
          }
          return;
        }
        return;
      }

      if (isFirstSetup) {
        const onClose = () => {
          factorSuccessModal.close();
        };
        factorSuccessModal.open({
          type: enrollFactorResponse.factorType,
          onClose,
        });
      } else {
        handleFactorActivated(enrollFactorResponse.factorType);
      }
    }
  }, [enrollFactorResponse, isFactorActivated]);

  const handleSkip = async () => {
    clear();
    try {
      await skipMfa({
        variables: {
          updateUserData: {
            askForMfa: false,
            stage: skipCommunicationPreferencesStep
              ? STAGE.FILLED
              : STAGE.COMMUNICATION_PREFERENCES,
          },
        },
      });
      const url = isFirstSetup ? routes.home : routes.securitySettings;
      skipCommunicationPreferencesStep &&
        navigate(url + location.search, {
          replace: true,
        });
    } catch (error) {
      showError(error);
    }
  };

  const handleEnrollUserFactor = async (
    factorType: string,
    provider: string,
    profile?: EnrollProfile
  ) => {
    clear();
    try {
      await enrollUserFactor({
        variables: {
          enrollFactorData: {
            factorType,
            provider,
            profile,
          },
        },
      });
      if (profile?.phoneNumber) {
        setIsUpdatePhoneAction(true);
      }
    } catch (error) {
      showError(error);
    }
  };

  const handleResendUserFactorCode = async () => {
    clear();
    if (!enrollFactorResponse) return;
    const { id: factorId, factorType, provider } = enrollFactorResponse;
    try {
      await resendUserFactorCode({
        variables: {
          resendFactorCodeData: {
            factorId,
            factorType,
            provider,
          },
        },
      });
    } catch (error) {
      showError();
    }
  };

  const handleActivateUserFactor = async (
    passCode: string,
    heapEventLocation?: HeapEventLocation
  ) => {
    clear();
    if (!enrollFactorResponse) return {};
    const { id: factorId, factorType } = enrollFactorResponse;
    const askForCommunicationPreferences =
      isFirstSetup && !skipCommunicationPreferencesStep;
    try {
      const response = await activateUserFactor({
        variables: {
          activateFactorData: {
            factorId,
            factorType,
            passCode,
          },
          heapEventLocation,
          askForCommunicationPreferences,
        },
      });
      return response;
    } catch (error) {
      if (isApolloError(error) && error.graphQLErrors?.length > 0) {
        const incorrectActivationCodeError = getGraphQLErrorBodies(error).find(
          (e) => e.code === ResultCodes.INCORRECT_ACTIVATION_CODE
        );
        // throw it to show in code form
        if (incorrectActivationCodeError) {
          throw incorrectActivationCodeError;
        } else {
          showError();
        }
      } else {
        showError();
      }
    }
    return {};
  };

  const handleDisableUserFactors = async () => {
    clear();
    try {
      return await disableUserFactors();
    } catch (error) {
      showError(error);
    }
    return {};
  };

  const handleFactorSetup = async (
    factor: Factor | undefined,
    isActive: boolean,
    isUserInMFAGroup: boolean
  ) => {
    if (!isFirstSetup) {
      const onClose = () => {
        factorDisableModal.close();
      };

      const onSubmit = async () => {
        try {
          await disableUserFactors().then(() => {
            resetActivatedFactor();
            onClose();
            navigate(routes.securitySettings, {
              replace: true,
              state: {
                factorDisabled: true,
              },
            });
          });
        } catch (error) {
          showError();
        }
      };
      if (!isActive && isUserInMFAGroup) {
        factorDisableModal.open({ onClose, onSubmit });
        return;
      }
    }
    await getProfile();
    if (factor) {
      if (
        factor.status === FactorStatus.ACTIVE &&
        factor.factorType !== FACTORS_TO_SHOW.email
      ) {
        handleFactorActivated(factor.factorType);
      } else {
        handleEnrollUserFactor(factor.factorType, factor.provider);
      }
    }
  };

  const resetActivatedFactor = () => {
    setIsFactorActivated(false);
    setEnrollFactorResponse(undefined);
  };

  const handleLoadIsUserInMFAGroup = () => {
    loadIsUserInMFAGroup();
  };

  const handleLoadEnrolledFactorList = async () => {
    const result = await loadEnrolledFactorList();
    return result?.data?.enrolledFactorList ?? [];
  };

  const isLoading =
    skipMfaIsLoading ||
    factorListIsLoading ||
    enrollUserIsLoading ||
    isUserInMFAGroupLoading ||
    resendUserFactorCodeIsLoading ||
    activateUserFactorIsLoading ||
    disableUserFactorsIsLoading ||
    enrolledFactorListIsLoading;

  const mfaSetupController: MfaSetupController = useMemo(
    () => ({
      isLoading,
      enrolledFactor: enrollFactorResponse,
      isFactorActivated,
      setIsFactorActivated,
      isUpdatePhoneAction,
      setIsUpdatePhoneAction,
      factorList: factorList?.factorList,
      enrolledFactorList: enrolledFactorList?.enrolledFactorList,
      isUserInMFAGroup: isUserInMFAGroup?.isUserInMFAGroup,
      loadIsUserInMFAGroup: handleLoadIsUserInMFAGroup,
      isFirstSetup,
      showError,
      loadFactorList,
      loadEnrolledFactorList: handleLoadEnrolledFactorList,
      handleSkip,
      handleFactorSetup,
      enrollUserFactor: handleEnrollUserFactor,
      resendUserFactorCode: handleResendUserFactorCode,
      activateUserFactor: handleActivateUserFactor,
      disableUserFactors: handleDisableUserFactors,
      resetActivatedFactor,
    }),
    [
      isLoading,
      enrollFactorResponse,
      isFactorActivated,
      setIsFactorActivated,
      isUpdatePhoneAction,
      setIsUpdatePhoneAction,
      factorList,
      enrolledFactorList,
      isUserInMFAGroup,
      handleLoadIsUserInMFAGroup,
      isFirstSetup,
      showError,
      loadFactorList,
      handleLoadEnrolledFactorList,
      handleSkip,
      handleFactorSetup,
      handleEnrollUserFactor,
      handleResendUserFactorCode,
      handleActivateUserFactor,
      handleDisableUserFactors,
      resetActivatedFactor,
    ]
  );

  return (
    <Context.Provider value={mfaSetupController}>
      {factorDisableModal.getDialog()}
      {factorSuccessModal.getDialog()}
      {children}
    </Context.Provider>
  );
};

export default MfaSetupProvider;
